import { useEffect, useState } from "react";
import { api, itemImage } from "@/lib/api";
import { weatherIcon } from "@/components/WeatherWidget";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { Plane, Loader2, Trash2, Luggage, Lightbulb, ChevronLeft, MapPin, CalendarRange } from "lucide-react";
import { toast } from "sonner";

const fmt = (iso) => new Date(iso + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });

export default function Trips() {
  const [trips, setTrips] = useState(null);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ destination: "", start_date: "", end_date: "", purpose: "", notes: "" });
  const [creating, setCreating] = useState(false);

  const load = () => { api.listTrips().then(setTrips).catch(() => setTrips([])); };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.destination || !form.start_date || !form.end_date) { toast.error("Destination and dates are required"); return; }
    setCreating(true);
    try {
      const t = await api.createTrip({ ...form, purpose: form.purpose || null, notes: form.notes || null });
      setTrips((prev) => [t, ...(prev || [])]);
      setActive(t);
      setForm({ destination: "", start_date: "", end_date: "", purpose: "", notes: "" });
      toast.success(`Packed for ${t.location_label}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not plan this trip");
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id) => {
    try { await api.deleteTrip(id); setTrips((p) => p.filter((t) => t.id !== id)); if (active?.id === id) setActive(null); toast.success("Trip removed"); }
    catch { toast.error("Could not remove"); }
  };

  if (active) return <TripDetail trip={active} onBack={() => { setActive(null); load(); }} onUpdate={setActive} onDelete={() => remove(active.id)} />;

  return (
    <div>
      <h1 className="font-display text-3xl sm:text-4xl tracking-[-0.02em]">Trips</h1>
      <p className="text-muted-foreground mt-1">Tell me where you're going — I'll check the weather there and pack a capsule from your closet.</p>

      <div className="mt-6 rounded-[var(--radius)] border border-border bg-card p-5" data-testid="trip-form">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="sm:col-span-3">
            <Label className="text-xs text-muted-foreground">Destination</Label>
            <Input value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} placeholder="e.g. Lisbon, Portugal" className="mt-1" data-testid="trip-destination-input" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">From</Label>
            <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1" data-testid="trip-start-input" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">To</Label>
            <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1" data-testid="trip-end-input" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">What kind of trip?</Label>
            <Input value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} placeholder="city break, wedding, work, beach…" className="mt-1" data-testid="trip-purpose-input" />
          </div>
          <div className="sm:col-span-3">
            <Label className="text-xs text-muted-foreground">Anything else? (optional)</Label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="two nice dinners, lots of walking, carry-on only" className="mt-1" data-testid="trip-notes-input" />
          </div>
        </div>
        <Button onClick={create} disabled={creating} className="mt-4 rounded-full" data-testid="trip-create-button">
          {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plane className="h-4 w-4 mr-2" />}
          {creating ? "Checking weather & packing…" : "Pack my trip"}
        </Button>
      </div>

      <h2 className="font-display text-2xl mt-10 mb-3">Your trips</h2>
      {trips === null ? (
        <div className="grid gap-3 sm:grid-cols-2">{[0, 1].map((i) => <Skeleton key={i} className="h-28 rounded-[var(--radius)]" />)}</div>
      ) : trips.length === 0 ? (
        <div className="rounded-[var(--radius)] border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="trips-empty">
          <Luggage className="h-7 w-7 mx-auto mb-2" /> No trips yet — plan your first one above.
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2" data-testid="trips-list">
          {trips.map((t) => {
            const packed = t.packed_item_ids?.length || 0;
            const total = t.pack_item_ids?.length || 0;
            return (
              <button key={t.id} onClick={() => setActive(t)} className="text-left rounded-[var(--radius)] border border-border bg-card p-4 hover:border-action transition-colors" data-testid={`trip-card-${t.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold truncate">{t.location_label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{fmt(t.start_date)} – {fmt(t.end_date)} · {t.days?.length || 0} days</p>
                  </div>
                  <div className="flex -space-x-2 shrink-0">
                    {(t.pack_items || []).slice(0, 4).map((i) => <img key={i.id} src={itemImage(i)} alt="" className="h-9 w-9 rounded-full object-cover ring-2 ring-card" />)}
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                  <Progress value={total ? (packed / total) * 100 : 0} className="h-1.5 flex-1" />
                  <span>{packed}/{total} packed</span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

const TripDetail = ({ trip, onBack, onUpdate, onDelete }) => {
  const packedItems = new Set(trip.packed_item_ids || []);
  const packedEss = new Set(trip.packed_essentials || []);
  const totalCount = (trip.pack_items?.length || 0) + (trip.essentials?.length || 0);
  const packedCount = packedItems.size + packedEss.size;

  const toggleItem = async (id) => {
    const next = packedItems.has(id) ? [...packedItems].filter((x) => x !== id) : [...packedItems, id];
    onUpdate({ ...trip, packed_item_ids: next });
    try { onUpdate(await api.updateTrip(trip.id, { packed_item_ids: next })); } catch { toast.error("Could not save"); }
  };
  const toggleEss = async (name) => {
    const next = packedEss.has(name) ? [...packedEss].filter((x) => x !== name) : [...packedEss, name];
    onUpdate({ ...trip, packed_essentials: next });
    try { onUpdate(await api.updateTrip(trip.id, { packed_essentials: next })); } catch { toast.error("Could not save"); }
  };

  return (
    <div data-testid="trip-detail">
      <button onClick={onBack} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="trip-back-button"><ChevronLeft className="h-4 w-4" /> All trips</button>
      <div className="mt-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-[-0.02em] flex items-center gap-2"><MapPin className="h-6 w-6 text-action" /> {trip.location_label}</h1>
          <p className="text-muted-foreground mt-1 flex items-center gap-1.5"><CalendarRange className="h-4 w-4" /> {fmt(trip.start_date)} – {fmt(trip.end_date)}{trip.purpose && <span>· {trip.purpose}</span>}</p>
        </div>
        <Button variant="ghost" size="sm" onClick={onDelete} className="rounded-full text-muted-foreground" data-testid="trip-delete-button"><Trash2 className="h-4 w-4 mr-1.5" /> Remove</Button>
      </div>

      {trip.summary && <p className="mt-4 text-sm text-muted-foreground max-w-3xl" data-testid="trip-summary">{trip.summary}</p>}

      {/* weather strip */}
      {trip.weather?.length > 0 && (
        <div className="mt-5 flex gap-2 overflow-x-auto pb-1" data-testid="trip-weather-strip">
          {trip.weather.map((d) => (
            <div key={d.date} className="shrink-0 rounded-xl border border-border bg-card px-3 py-2 text-center min-w-[88px]">
              <p className="text-[11px] text-muted-foreground">{fmt(d.date)}</p>
              <div className="flex justify-center my-1">{weatherIcon(d.code, "h-5 w-5")}</div>
              <p className="text-xs font-mono-plex">{Math.round(d.temp_max)}°/{Math.round(d.temp_min)}°</p>
              {d.estimate && <p className="text-[9px] text-muted-foreground">typical</p>}
            </div>
          ))}
        </div>
      )}

      <div className="mt-8 grid gap-8 lg:grid-cols-[1.3fr_1fr]">
        {/* day by day */}
        <section>
          <h2 className="font-display text-2xl mb-3">Day by day</h2>
          <div className="space-y-3">
            {trip.days.map((d, i) => (
              <div key={d.date || i} className="rounded-[var(--radius)] border border-border bg-card p-4" data-testid={`trip-day-${i}`}>
                <div className="flex items-center justify-between">
                  <p className="font-semibold">{d.date ? fmt(d.date) : `Day ${i + 1}`}</p>
                  {trip.weather?.find((w) => w.date === d.date) && <span className="text-xs text-muted-foreground">{trip.weather.find((w) => w.date === d.date).description}</span>}
                </div>
                <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
                  {(d.items || []).map((it) => (
                    <div key={it.id} className="shrink-0 w-20" title={it.name}>
                      <div className="aspect-[3/4] rounded-lg overflow-hidden bg-secondary"><img src={itemImage(it)} alt={it.name} className="h-full w-full object-cover" /></div>
                      <p className="text-[10px] truncate mt-1">{it.name}</p>
                    </div>
                  ))}
                </div>
                {d.note && <p className="text-xs text-muted-foreground mt-2">{d.note}</p>}
              </div>
            ))}
          </div>
        </section>

        {/* pack list */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-2xl flex items-center gap-2"><Luggage className="h-5 w-5" /> Pack list</h2>
            <Badge variant="secondary" data-testid="trip-packed-count">{packedCount}/{totalCount} packed</Badge>
          </div>
          <Progress value={totalCount ? (packedCount / totalCount) * 100 : 0} className="h-1.5 mb-4" />
          <div className="rounded-[var(--radius)] border border-border bg-card divide-y divide-border">
            {trip.pack_items.map((it) => (
              <label key={it.id} className={`flex items-center gap-3 p-3 cursor-pointer ${packedItems.has(it.id) ? "opacity-60" : ""}`} data-testid={`trip-pack-item-${it.id}`}>
                <Checkbox checked={packedItems.has(it.id)} onCheckedChange={() => toggleItem(it.id)} />
                <img src={itemImage(it)} alt="" className="h-10 w-10 rounded-lg object-cover" />
                <div className="min-w-0 flex-1">
                  <p className={`text-sm truncate ${packedItems.has(it.id) ? "line-through" : ""}`}>{it.name}</p>
                  <p className="text-[11px] text-muted-foreground capitalize">{it.category}{it.brand ? ` · ${it.brand}` : ""}</p>
                </div>
              </label>
            ))}
          </div>
          {trip.essentials?.length > 0 && (
            <>
              <h3 className="text-sm font-medium mt-5 mb-2">Essentials</h3>
              <div className="rounded-[var(--radius)] border border-border bg-card divide-y divide-border">
                {trip.essentials.map((e) => (
                  <label key={e} className={`flex items-center gap-3 p-3 cursor-pointer ${packedEss.has(e) ? "opacity-60" : ""}`}>
                    <Checkbox checked={packedEss.has(e)} onCheckedChange={() => toggleEss(e)} />
                    <span className={`text-sm ${packedEss.has(e) ? "line-through" : ""}`}>{e}</span>
                  </label>
                ))}
              </div>
            </>
          )}
          {trip.tips?.length > 0 && (
            <div className="mt-5 rounded-[var(--radius)] bg-secondary/60 p-4">
              <p className="text-xs font-medium flex items-center gap-1.5 mb-2"><Lightbulb className="h-3.5 w-3.5 text-action" /> Packing tips</p>
              <ul className="text-xs text-muted-foreground space-y-1 list-disc pl-4">{trip.tips.map((t) => <li key={t}>{t}</li>)}</ul>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};
